import React, { useState } from 'react';

import FadeIn from 'react-fade-in';
import { mdiClose } from '@mdi/js';
import { Icon } from '@mdi/react';

import { useProducerWithThunks } from 'common/hooks/useProducer';

import { TransactionMode } from 'features/banking/customerBanking/customerBanking';

import {
  ThemedCardLevel1,
  CardContents,
  TitleContainer,
  CardTitle,
  ButtonContainer,
  AmountInput,
  InputBox,
  InputContainer,
  PrimaryButton,
  SecondaryButton,
  CancelButton,
  FormError,
  Selector,
} from 'common/components/cardStyles';
import { ExternalBankAccount, Wallet } from 'common/state/banking/actions';
import { depositRecipe, withdrawalRecipe } from 'common/state/banking/recipes';
import {
  defaultDepositState,
  defaultWithdrawalState,
} from 'common/state/banking/states';
import { doDeposit, doWithdrawal } from 'common/state/banking/thunkActions';

interface TransferCard {
  setTransactionMode: React.Dispatch<React.SetStateAction<TransactionMode>>;
  wallets: Wallet[];
  bankAccounts: ExternalBankAccount[];
}

export function DepositCard({
  setTransactionMode,
  wallets,
  bankAccounts,
}: TransferCard) {
  // The reference and amount input fields
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');

  // The options available for the wallet selector
  const walletOptions = React.useMemo(
    () => wallets.map((wallet) => ({ value: wallet, label: wallet.title })),
    [wallets]
  );
  // The currently selected wallet
  const [selectedWallet, setSelectedWallet] = useState<null | ElementType<
    typeof walletOptions
  >>(null);

  // The options available for the account selector
  // Options change if a wallet of a different currenct is selected
  const bankAccountOptions = React.useMemo(
    () =>
      (bankAccounts || [])
        .filter((account) =>
          selectedWallet?.value?.currency &&
          account.currency !== selectedWallet.value.currency
            ? false
            : true
        )
        .map((account) => ({ value: account, label: account.title })),
    [bankAccounts, selectedWallet]
  );
  // The currently selected bank account
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<null | ElementType<typeof bankAccountOptions>>(null);

  // The state of the deposit request
  const [submitDepositState, submitDepositDispatch] = useProducerWithThunks(
    depositRecipe,
    defaultDepositState
  );
  // Deposit request callback for form
  const submitDeposit: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      submitDepositDispatch(
        doDeposit({
          wallet: selectedWallet.value.url,
          destination: selectedBankAccount.value.url,
          reference,
          currency: selectedWallet.value.currency,
          amount: parseInt(amount),
        })
      );
    },
    [
      amount,
      reference,
      selectedBankAccount,
      selectedWallet,
      submitDepositDispatch,
    ]
  );

  // Function that closes the card
  const cancelTransaction: React.MouseEventHandler = React.useCallback(
    () => setTransactionMode(null),
    [setTransactionMode]
  );

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setAmount('');
    setReference('');
    setSelectedWallet(null);
    setSelectedBankAccount(null);
  }, [setSelectedWallet, setAmount]);

  // Clear selected bank account if wallet with different currency selected
  React.useEffect(() => {
    if (selectedWallet?.value.currency !== selectedBankAccount?.value.currency)
      setSelectedBankAccount(null);
  }, [selectedWallet, selectedBankAccount]);

  return (
    <form onSubmit={submitDeposit} style={{ display: 'inline-block' }}>
      <FadeIn>
        <ThemedCardLevel1>
          <CardContents>
            <TitleContainer>
              <CardTitle>Deposit</CardTitle>
              <CancelButton onClick={cancelTransaction}>
                <Icon size={0.8} path={mdiClose} />
              </CancelButton>
            </TitleContainer>
            {submitDepositState.error && (
              <FormError>{submitDepositState.error}</FormError>
            )}
            <InputContainer>
              <Selector
                label="Wallet *"
                shrinked
                options={walletOptions}
                value={selectedWallet}
                onChange={(e) => setSelectedWallet(e)}
              />
              <Selector
                label="Destination *"
                shrinked
                options={bankAccountOptions}
                value={selectedBankAccount}
                onChange={(e) => setSelectedBankAccount(e)}
              />
            </InputContainer>
            <InputContainer>
              <InputBox
                label="Reference *"
                shrinked
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <AmountInput
                currency={
                  selectedWallet ? selectedWallet.value.currency : 'NONE'
                }
                shrinked
                disabled={!selectedWallet || !selectedBankAccount}
                label="Amount *"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </InputContainer>
            {!submitDepositState?.pending &&
              !submitDepositState.error &&
              submitDepositState?.deposit && (
                <div>Successfully submitted deposit.</div>
              )}
            <ButtonContainer>
              <PrimaryButton
                type="submit"
                disabled={
                  !selectedWallet ||
                  !selectedBankAccount ||
                  !reference ||
                  !amount
                }
              >
                Create
              </PrimaryButton>
              <SecondaryButton type="button" onClick={clearTransaction}>
                Clear All
              </SecondaryButton>
            </ButtonContainer>
          </CardContents>
        </ThemedCardLevel1>
      </FadeIn>
    </form>
  );
}

export function WithdrawCard({ setTransactionMode, wallets }: TransferCard) {
  const [reference, setReference] = useState('');
  // The amount input field
  const [amount, setAmount] = useState('');

  // The options available for the wallet selector
  const walletOptions = React.useMemo(
    () => wallets.map((wallet) => ({ value: wallet, label: wallet.title })),
    [wallets]
  );
  // The currently selected wallet
  const [selectedWallet, setSelectedWallet] = useState<null | ElementType<
    typeof walletOptions
  >>(null);

  // The state of the withdraw request
  const [submitWithdrawState, submitWithdrawDispatch] = useProducerWithThunks(
    withdrawalRecipe,
    defaultWithdrawalState
  );
  // Withdraw submission callback for form
  const submitWithdraw: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      submitWithdrawDispatch(
        doWithdrawal({
          wallet: selectedWallet.value.url,
          currency: selectedWallet.value.currency,
          reference,
          amount: parseInt(amount),
        })
      );
    },
    [amount, reference, selectedWallet, submitWithdrawDispatch]
  );

  // Function that closes the card
  const cancelTransaction: React.MouseEventHandler = React.useCallback(
    () => setTransactionMode(null),
    [setTransactionMode]
  );

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setAmount('');
    setSelectedWallet(null);
    setReference('');
  }, [setSelectedWallet, setAmount]);

  return (
    <form onSubmit={submitWithdraw} style={{ display: 'inline-block' }}>
      <FadeIn>
        <ThemedCardLevel1>
          <CardContents>
            <TitleContainer>
              <CardTitle>Withdraw</CardTitle>
              <CancelButton onClick={cancelTransaction}>
                <Icon size={0.8} path={mdiClose} />
              </CancelButton>
            </TitleContainer>
            {submitWithdrawState.error && (
              <FormError>{submitWithdrawState.error}</FormError>
            )}
            <InputContainer>
              <Selector
                label="Wallet *"
                shrinked
                options={walletOptions}
                value={selectedWallet}
                onChange={(e) => setSelectedWallet(e)}
              />
            </InputContainer>
            <InputContainer>
              <InputBox
                label="Reference *"
                shrinked
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <AmountInput
                label="Amount *"
                currency={
                  selectedWallet ? selectedWallet.value.currency : 'NONE'
                }
                shrinked
                disabled={!selectedWallet}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </InputContainer>
            {!submitWithdrawState?.pending &&
              !submitWithdrawState.error &&
              submitWithdrawState?.withdrawal && (
                <div>Successfully submitted withdrawal.</div>
              )}
            <ButtonContainer>
              <PrimaryButton
                type="submit"
                disabled={!selectedWallet || !amount}
              >
                Create
              </PrimaryButton>
              <SecondaryButton type="button" onClick={clearTransaction}>
                Clear All
              </SecondaryButton>
            </ButtonContainer>
          </CardContents>
        </ThemedCardLevel1>
      </FadeIn>
    </form>
  );
}
